import { reportAsyncError } from "@istari-frontend/shared";
import type { V3 } from "@istari/istari-client";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { agentQueryOptions } from "~/api/agent/agent-query-options";
import type { ChangeRequestId } from "~/change-request/model/change-request";
import { type Notification, shortId } from "~/notifications/model/notification";
import { resourceQueryOptions } from "~/resource/data-source/api/resource-query-options";
import { userQueryOptions } from "~/user/data-source/user-query-options";
import { bucketWorkflowLogStatus } from "~/workflow-log/model/workflow-log-outcome";

import {
  hasEventMetadata,
  type ReceivedResourceEventData,
  toJobStatus,
  toReceivedResourcePayload,
  toSharedResourceType,
  toSharedRole,
  type WithEventMetadata,
} from "./api/inbox-adapter";
import { notificationPollingQueryOptions } from "./api/notification-polling-query-options";
import { getAgentFallbackId, toNotificationActorName } from "./resolve-notification-actor-name";
import { type ChangeRequestDetail, useChangeRequestEnrichment } from "./use-change-request-enrichment";
import { type MissingEventMetadata, useReportMissingEventMetadata } from "./use-report-missing-event-metadata";
import {
  type UnrecognizedNotificationEvent,
  useReportUnrecognizedNotificationEvents,
} from "./use-report-unrecognized-events";
import {
  type UnrecognizedWorkflowLogStatus,
  useReportUnrecognizedWorkflowLogStatuses,
} from "./use-report-unrecognized-workflow-log-statuses";
import { useWorkflowLogEnrichment, type WorkflowLogDetail } from "./use-workflow-log-enrichment";

export function useNotificationPolling(options?: { enabled?: boolean }) {
  const pollingQuery = useQuery({
    ...notificationPollingQueryOptions.list(),
    enabled: options?.enabled,
  });

  const shareEvents = (pollingQuery.data?.items ?? [])
    .filter((item) => item.data.eventType === "notification.user.access.granted")
    .map((item) => item.data as V3.UserAccessGrantedNotificationEvent);

  // combine applies replaceEqualDeep to the result, keeping stable references when data is unchanged.
  // This prevents useMemo below from recomputing on every render.
  const resourceDetails = useQueries({
    queries: shareEvents.map((event) => resourceQueryOptions.byId(event.resourceId)),
    combine: (results) =>
      results.map((result, index) => {
        if (result.status === "error") {
          reportAsyncError("notifications-resource-lookup-failed", result.error, {
            resourceId: shareEvents[index].resourceId,
          });
          return { name: null, resourceType: null, createdById: null };
        }
        return {
          name: result.data?.name ?? null,
          resourceType: result.data?.resourceType ?? null,
          createdById: result.data?.createdById ?? null,
        };
      }),
  });

  const createdByIds = resourceDetails.map((rd) => rd.createdById);

  const actorUsers = useQueries({
    queries: createdByIds.map((createdById) => ({
      ...userQueryOptions.byId(createdById ?? ""),
      enabled: !!createdById,
    })),
    combine: (results) =>
      results.map((result, index) => {
        if (result.status === "error") {
          reportAsyncError("notifications-actor-lookup-failed", result.error, { userId: createdByIds[index] });
          return null;
        }
        return result.data ?? null;
      }),
  });

  const agentFallbackIds = useMemo(() => actorUsers.map(getAgentFallbackId), [actorUsers]);

  const agentDisplayNames = useQueries({
    queries: agentFallbackIds.map((agentId) => ({
      ...agentQueryOptions.byId(agentId ?? ""),
      enabled: !!agentId,
    })),
    combine: (results) =>
      results.map((result, index) => {
        if (result.status === "error") {
          reportAsyncError("notifications-agent-lookup-failed", result.error, { agentId: agentFallbackIds[index] });
          return null;
        }
        return result.data?.displayName ?? null;
      }),
  });

  const actorNames = useMemo(
    () => actorUsers.map((user, index) => toNotificationActorName(user) ?? agentDisplayNames[index] ?? null),
    [actorUsers, agentDisplayNames],
  );

  const changeRequestEvents = (pollingQuery.data?.items ?? [])
    .filter((item) => item.data.eventType === "notification.user.change_request.opened")
    .map((item) => item.data as V3.SystemChangeRequestOpenedNotificationEvent);

  const { changeRequestDetails, requesterNames } = useChangeRequestEnrichment(changeRequestEvents);

  const workflowLogEvents = (pollingQuery.data?.items ?? [])
    .filter((item) => item.data.eventType === "notification.user.workflow_log.completed")
    .map((item) => item.data as V3.UserWorkflowLogCompletedNotificationEvent);

  const { workflowLogDetails, workflowLogCreatorNames, workflowLogSystemNames } =
    useWorkflowLogEnrichment(workflowLogEvents);

  const built = useMemo(
    () =>
      buildNotifications(pollingQuery.data?.items ?? [], {
        actorNames,
        resourceDetails,
        requesterNames,
        changeRequestDetails,
        workflowLogCreatorNames,
        workflowLogDetails,
        workflowLogSystemNames,
      }),
    [
      pollingQuery.data,
      actorNames,
      resourceDetails,
      requesterNames,
      changeRequestDetails,
      workflowLogCreatorNames,
      workflowLogDetails,
      workflowLogSystemNames,
    ],
  );

  useReportUnrecognizedNotificationEvents(built.unrecognized);
  useReportUnrecognizedWorkflowLogStatuses(built.unrecognizedWorkflowLogStatuses);
  useReportMissingEventMetadata(built.missingMetadata);

  return { ...pollingQuery, data: pollingQuery.data ? built.notifications : undefined };
}

interface PollingEnrichment {
  actorNames: (string | null)[];
  resourceDetails: { name: string | null; resourceType: V3.ResourceTypeDto | null; createdById: string | null }[];
  requesterNames: (string | null)[];
  changeRequestDetails: ChangeRequestDetail[];
  workflowLogCreatorNames: (string | null)[];
  workflowLogDetails: WorkflowLogDetail[];
  workflowLogSystemNames: (string | null)[];
}

function buildNotifications(
  rawItems: V3.NotificationDto[],
  enrichment: PollingEnrichment,
): {
  notifications: Notification[];
  unrecognized: UnrecognizedNotificationEvent[];
  unrecognizedWorkflowLogStatuses: UnrecognizedWorkflowLogStatus[];
  missingMetadata: MissingEventMetadata[];
} {
  const notifications: Notification[] = [];
  const unrecognized: UnrecognizedNotificationEvent[] = [];
  const unrecognizedWorkflowLogStatuses: UnrecognizedWorkflowLogStatus[] = [];
  const missingMetadata: MissingEventMetadata[] = [];
  let shareIdx = 0;
  let changeRequestIdx = 0;
  let workflowLogIdx = 0;

  for (const item of rawItems) {
    const { data } = item;

    switch (data.eventType) {
      case "notification.user.access.granted": {
        const actorName = enrichment.actorNames[shareIdx] ?? null;
        const resource = enrichment.resourceDetails[shareIdx] ?? null;
        shareIdx++;
        if (!hasEventMetadata(data)) {
          missingMetadata.push({ eventType: "notification.user.access.granted", itemId: data.resourceId });
          break;
        }
        notifications.push(toShareNotification(data, actorName, resource));
        break;
      }
      case "notification.user.job.terminal_state": {
        if (!hasEventMetadata(data)) {
          missingMetadata.push({ eventType: "notification.user.job.terminal_state", itemId: data.jobId });
          break;
        }
        const notification = toJobNotification(data);
        if (notification) notifications.push(notification);
        break;
      }
      case "notification.user.received_resource.shared":
      case "notification.user.received_resource.updated":
      case "notification.user.received_resource.revoked": {
        const notification = toReceivedResourceNotification(data);
        if (notification) notifications.push(notification);
        break;
      }
      case "notification.user.change_request.opened": {
        const requesterName = enrichment.requesterNames[changeRequestIdx] ?? null;
        const changeRequest = enrichment.changeRequestDetails[changeRequestIdx] ?? null;
        changeRequestIdx++;
        if (!hasEventMetadata(data)) {
          missingMetadata.push({ eventType: "notification.user.change_request.opened", itemId: data.changeRequestId });
          break;
        }
        notifications.push(toPolledChangeRequestNotification(data, requesterName, changeRequest));
        break;
      }
      case "notification.user.workflow_log.completed": {
        const index = workflowLogIdx;
        workflowLogIdx++;
        if (!hasEventMetadata(data)) {
          missingMetadata.push({
            eventType: "notification.user.workflow_log.completed",
            itemId: data.workflowLogEntryId,
          });
          break;
        }
        const { notification, unrecognizedStatus } = buildPolledWorkflowLog(data, enrichment, index);
        notifications.push(notification);
        if (unrecognizedStatus) unrecognizedWorkflowLogStatuses.push(unrecognizedStatus);
        break;
      }
      default: {
        const _exhaustive: never = data;
        unrecognized.push({ eventType: item.eventType });
      }
    }
  }

  return { notifications, unrecognized, unrecognizedWorkflowLogStatuses, missingMetadata };
}

function buildPolledWorkflowLog(
  data: WithEventMetadata<V3.UserWorkflowLogCompletedNotificationEvent>,
  enrichment: PollingEnrichment,
  index: number,
): { notification: Notification; unrecognizedStatus: UnrecognizedWorkflowLogStatus | null } {
  const notification = toPolledWorkflowLogNotification(
    data,
    enrichment.workflowLogCreatorNames[index] ?? null,
    enrichment.workflowLogDetails[index] ?? null,
    enrichment.workflowLogSystemNames[index] ?? null,
  );
  const unrecognizedStatus =
    bucketWorkflowLogStatus(data.status) === "unspecified"
      ? { status: data.status, workflowLogEntryId: data.workflowLogEntryId }
      : null;
  return { notification, unrecognizedStatus };
}

function toReceivedResourceNotification(data: ReceivedResourceEventData): Notification | null {
  const payload = toReceivedResourcePayload(data);
  if (!payload) return null;

  return {
    id: data.eventId ?? `${data.eventType}:${data.resourceId}:${data.occurredAt.toISOString()}`,
    type: "received-resource",
    createdAt: data.createdAt ?? data.occurredAt,
    status: "unread",
    payload,
  };
}

function toShareNotification(
  data: WithEventMetadata<V3.UserAccessGrantedNotificationEvent>,
  actorName: string | null,
  resource: { name: string | null; resourceType: V3.ResourceTypeDto | null; createdById: string | null } | null,
): Notification {
  const actorId = resource?.createdById ?? data.grantedUserId;
  const displayName = actorName ?? "Unknown user";

  return {
    id: `${data.grantedUserId}:${data.resourceId}:${data.eventId}`,
    type: "share",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: { userId: actorId, displayName },
    payload: {
      resourceType: toSharedResourceType(resource?.resourceType) ?? "file",
      resourceId: data.resourceId,
      resourceName: resource?.name ?? data.resourceId,
      role: toSharedRole(data.permissionLevel),
      shareCount: 1,
    },
  };
}

function toPolledChangeRequestNotification(
  data: WithEventMetadata<V3.SystemChangeRequestOpenedNotificationEvent>,
  requesterName: string | null,
  changeRequest: ChangeRequestDetail | null,
): Notification {
  return {
    id: `change-request:${data.changeRequestId}:${data.eventId}`,
    type: "changeRequest",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: {
      userId: changeRequest?.createdById ?? null,
      displayName: requesterName ?? "Unknown user",
    },
    payload: {
      changeRequestId: data.changeRequestId as ChangeRequestId,
      systemId: data.systemId,
      title: changeRequest?.title ?? shortId(data.changeRequestId),
    },
  };
}

function toPolledWorkflowLogNotification(
  data: WithEventMetadata<V3.UserWorkflowLogCompletedNotificationEvent>,
  creatorName: string | null,
  entry: WorkflowLogDetail | null,
  systemName: string | null,
): Notification {
  return {
    id: `workflow-log:${data.workflowLogEntryId}:${data.eventId}`,
    type: "workflowLog",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: {
      userId: entry?.createdById ?? null,
      displayName: creatorName ?? "Unknown user",
    },
    payload: {
      workflowLogEntryId: data.workflowLogEntryId,
      systemId: data.systemId,
      systemName,
      status: data.status,
      outcome: bucketWorkflowLogStatus(data.status),
      title: entry?.title ?? shortId(data.workflowLogEntryId),
    },
  };
}

function toJobNotification(data: WithEventMetadata<V3.UserJobTerminalStateNotificationEvent>): Notification | null {
  const jobStatus = toJobStatus(data.terminalState);
  if (!jobStatus) return null;

  return {
    id: `job:${data.jobId}:${data.eventId}`,
    type: "job",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: { userId: "system", displayName: "Build Pipeline" },
    payload: {
      jobId: data.jobId,
      jobStatus,
      functionName: `Job ${data.jobId.slice(0, 8)}`,
    },
  };
}
